import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Box, Tabs, TabList, Tab, Spinner, Tooltip, Icon, useBreakpointValue
} from "@chakra-ui/react";
import { MdDescription, MdInfo, MdCalendarMonth } from "react-icons/md";
import Details from "../services/details";
import ContactDetails from "./ContactDetails";
import Reservation from "./reservation";
import {
    StyledCard,
    StyledCardContent
} from "../../../stats/sharedCardStyles";

/**
 * Builds the dynamic style props for each Chakra Tab element.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props for the Tab
 */
function buildTabProps(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.6)",
        minWidth: isMobile ? "56px" : "80px",
        minHeight: isMobile ? "48px" : "56px",
        padding: isMobile ? "10px 14px" : "14px 18px",
        transition: "all 0.3s ease",
        borderRadius: 0,
        _hover: { color: "rgba(255, 255, 255, 0.95)", background: "rgba(255, 255, 255, 0.08)" },
        _selected: {
            color: "#17dcb8", fontWeight: 600,
            background: "rgba(23, 220, 184, 0.08)",
            borderBottom: "3px solid #17dcb8"
        }
    };
}

/**
 * Renders the tab navigation bar with icon-based tabs and tooltips.
 * @param {Object} props
 * @param {number} props.tabActive - The currently selected tab index
 * @param {Function} props.onTabChange - Callback when tab selection changes
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ContactCardTabs({ tabActive, onTabChange, isMobile }) {
    const tabProps = buildTabProps(isMobile);
    return (
        <Box borderBottom="1px solid rgba(255, 255, 255, 0.12)"
            background="rgba(255, 255, 255, 0.04)" backdropFilter="blur(10px)">
            <Tabs index={tabActive} onChange={onTabChange}
                variant="unstyled" isFitted={!isMobile}>
                <TabList minHeight={isMobile ? "48px" : "56px"}>
                    <Tooltip label="Prise de contact" hasArrow placement="top">
                        <Tab {...tabProps}><Icon as={MdInfo} boxSize={isMobile ? "1.3rem" : "1.5rem"} /></Tab>
                    </Tooltip>
                    <Tooltip label="Réservation associée" hasArrow placement="top">
                        <Tab {...tabProps}><Icon as={MdCalendarMonth} boxSize={isMobile ? "1.3rem" : "1.5rem"} /></Tab>
                    </Tooltip>
                    <Tooltip label="Fiche de prestation" hasArrow placement="top">
                        <Tab {...tabProps}><Icon as={MdDescription} boxSize={isMobile ? "1.3rem" : "1.5rem"} /></Tab>
                    </Tooltip>
                </TabList>
            </Tabs>
        </Box>
    );
}

/** Style props for the scrollable content wrapper. */
const scrollableWrapperProps = {
    maxHeight: "calc(100vh - 80px)",
    overflowY: "auto",
    sx: {
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-track": { background: "rgba(255, 255, 255, 0.05)", borderRadius: "10px" },
        "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            borderRadius: "10px"
        }
    }
};

/**
 * Renders a centered loading spinner for the service tab.
 * @returns {React.ReactElement}
 */
function ServiceLoadingSpinner() {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Spinner boxSize="40px" color="rgba(255, 255, 255, 0.8)" />
        </Box>
    );
}

/**
 * Renders the active tab content based on the selected tab index.
 * @param {Object} props
 * @param {number} props.tabActive - The currently selected tab index
 * @param {Object} props.contactCard - The contact card data object
 * @param {string} props.currency - Currency symbol
 * @param {boolean} props.loading - Whether the service is loading
 * @param {Object} props.service - The loaded service data
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ContactCardContent({ tabActive, contactCard, currency, loading, service, isMobile }) {
    return (
        <StyledCardContent padding={isMobile ? "16px" : "24px"}>
            <Box {...scrollableWrapperProps}>
                {tabActive === 0 && (
                    <ContactDetails contactCard={contactCard} currency={currency} />
                )}
                {tabActive === 1 && (
                    <Reservation reservation={contactCard?.reservation} />
                )}
                {tabActive === 2 && (
                    loading ? <ServiceLoadingSpinner /> : <Details serviceCountry={currency} service={service} />
                )}
            </Box>
        </StyledCardContent>
    );
}

/**
 * Fetches the artist service data for a given contact card.
 * @param {Object} contactCard - The contact card with reservation.services_id
 * @param {Function} setService - State setter for the service data
 * @param {Function} setLoading - State setter for the loading flag
 * @returns {Promise<void>}
 */
async function fetchService(contactCard, setService, setLoading) {
    setLoading(true);
    try {
        const response = await axios.get('/artist_services/' + contactCard?.reservation.services_id);
        setService(response.data.service);
    } catch (error) {
        console.error("Error fetching service:", error);
    } finally {
        setLoading(false);
    }
}

/**
 * Main contact card component with tabbed navigation for details, reservation, and service.
 * @param {Object} props
 * @param {Object} props.contactCard - The contact card data object
 * @param {string} props.currency - Currency symbol to display
 * @returns {React.ReactElement}
 */
function ContactCard({ contactCard, currency }) {
    const [service, setService] = useState(null);
    const [tabActive, setTabActive] = useState(0);
    const [loading, setLoading] = useState(false);
    const isMobile = useBreakpointValue({ base: true, sm: false });

    useEffect(() => {
        if (tabActive === 2 && !service) {
            fetchService(contactCard, setService, setLoading).then(r => null);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [tabActive, service, contactCard]);

    return (
        <StyledCard>
            <ContactCardTabs tabActive={tabActive} onTabChange={setTabActive} isMobile={isMobile} />
            <ContactCardContent
                tabActive={tabActive} contactCard={contactCard} currency={currency}
                loading={loading} service={service} isMobile={isMobile}
            />
        </StyledCard>
    );
}

export default ContactCard;
